import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ESignDetailsModal from '../components/Common/ESignDetailsModal';
import CvitpDrawer from './CvitpDrawer';
import { getApiUrl } from '../authConfig';
import { calculateAssigneeStats } from '../utils/StatsHelper';
import { useTaxPortal } from '../utils/useTaxPortal';
import TaxPortalLayout from '../components/Common/TaxPortalLayout';
import TaxPortalToolbar from '../components/Common/TaxPortalToolbar';
import Loader from '../components/Common/Loader';
import { getValidToken } from '../utils/msalTokenUtil';
import { isAdminRole } from '../authConfig';
import {
  FaChartBar, FaPhoneAlt, FaEdit, FaFolderOpen, FaCheckCircle, FaUsers, FaSignature, 
  FaHourglassHalf, FaClipboardList, FaPaperPlane, FaFileSignature, FaExclamationCircle,
  FaChevronLeft, FaChevronRight, FaInbox, FaSort, FaSortUp,
  FaSortDown, FaEllipsisV, FaTimes, FaInfoCircle, FaChevronUp, FaChevronDown,
  FaCalendarAlt, FaCalendarCheck, FaUserPlus, FaEnvelope,
} from 'react-icons/fa';

const statusOptions = ['Pending', 'Draft Sent', 'Processing', 'eSigned', 'On Hold', 'Completed', 'Cancelled'];

const STATUS_META = {
  Pending:      { color: '#b45309', bg: '#fef3c7', icon: FaHourglassHalf },
  'Draft Sent': { color: '#0e7490', bg: '#cffafe', icon: FaPaperPlane },
  Processing:   { color: '#1d4ed8', bg: '#dbeafe', icon: FaClipboardList },
  eSigned:      { color: '#6d28d9', bg: '#ede9fe', icon: FaFileSignature },
  'On Hold':    { color: '#b91c1c', bg: '#fee2e2', icon: FaExclamationCircle },
  Completed:    { color: '#15803d', bg: '#dcfce7', icon: FaCheckCircle },
  Cancelled:    { color: '#64748b', bg: '#f1f5f9', icon: FaTimes },
};

const getStatusMeta = (status) => STATUS_META[status] || { color: '#64748b', bg: '#f1f5f9', icon: FaFolderOpen };

const StatusBadge = ({ status, size = 'md' }) => {
  const meta = getStatusMeta(status);
  const Icon = meta.icon;
  const isSm = size === 'sm';
  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill fw-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg, fontSize: isSm ? '0.7rem' : '0.75rem', padding: isSm ? '3px 10px' : '5px 12px', lineHeight: 1.4, whiteSpace: 'nowrap' }}
    >
      <Icon size={isSm ? 9 : 10} />{status || 'Unknown'}
    </span>
  );
};

const SortIcon = ({ active, asc }) => {
  if (!active) return <FaSort className="ms-1 opacity-50" size={11} />;
  return asc ? <FaSortUp className="ms-1" size={11} /> : <FaSortDown className="ms-1" size={11} />;
};

// Avatar helpers
const AVATAR_COLORS = [
  { bg: '#dbeafe', text: '#1e40af' }, { bg: '#fce7f3', text: '#9d174d' },
  { bg: '#d1fae5', text: '#065f46' }, { bg: '#ede9fe', text: '#5b21b6' },
  { bg: '#fef3c7', text: '#92400e' }, { bg: '#cffafe', text: '#164e63' },
  { bg: '#fee2e2', text: '#991b1b' }, { bg: '#e0f2fe', text: '#0c4a6e' },
];
const getAvatarColor = (name = '') => {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};
const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'C').substring(0, 2).toUpperCase();
};

const AVATAR_SIZE = 32;
const AVATAR_GAP  = 8;
const CELL_PS     = 16; // ps-3
const NAME_TH_PL  = CELL_PS + AVATAR_SIZE + AVATAR_GAP; // 56px

const ClientAvatar = ({ name }) => {
  const c = getAvatarColor(name);
  return (
    <span
      className="name-avatar flex-shrink-0"
      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, backgroundColor: c.bg, color: c.text }}
      aria-hidden="true"
    >
      {getInitials(name)}
    </span>
  );
};

// Skeleton rows
const SkeletonRows = ({ count = 7 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="skeleton-row">
        <td style={{ paddingLeft: CELL_PS }}>
          <div className="d-flex align-items-center gap-2">
            <span className="skeleton-cell" style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, borderRadius: '50%' }} />
            <div>
              <span className="skeleton-cell d-block mb-1" style={{ width: 100 + (i % 3) * 25, height: 13 }} />
              <span className="skeleton-cell d-block" style={{ width: 80, height: 11 }} />
            </div>
          </div>
        </td>
        <td><span className="skeleton-cell" style={{ width: 90, height: 13 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 60, height: 22, borderRadius: 99 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 80, height: 22, borderRadius: 99 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 90, height: 13 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 70, height: 28, borderRadius: 99 }} /></td>
      </tr>
    ))}
  </>
);

// Format date nicely
const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const CvitpPage = () => {
  const portalState = useTaxPortal();
  const {
    account, msalInstance, isInitialized,
    setDialNumber, isDialerOpen, setIsDialerOpen,
    setRefreshTrigger, setEmailModalConfig,
  } = portalState;

  const navigate = useNavigate();

  const [taxEntries,       setTaxEntries]       = useState([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [eSignDetails,     setESignDetails]     = useState(null);
  const [searchText,       setSearchText]       = useState('');
  const [sortKey,          setSortKey]          = useState('receivedDate');
  const [sortAsc,          setSortAsc]          = useState(false);
  const [filterStatus,     setFilterStatus]     = useState('nonCompleted');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [editingEntry,     setEditingEntry]     = useState(null);
  const [isDrawerOpen,     setIsDrawerOpen]     = useState(false);
  const [isSaving,         setIsSaving]         = useState(false);
  const [pageSize,         setPageSize]         = useState(25);
  const [pageIndex,        setPageIndex]        = useState(0);
  const [showOpenDetails,  setShowOpenDetails]  = useState(false);
  const [showDoneDetails,  setShowDoneDetails]  = useState(false);
  const [activeTooltipId,  setActiveTooltipId]  = useState(null);

  const agentEmails = useMemo(() =>
    (process.env.REACT_APP_USER_EMAILS || '').split(',').map(e => e.trim()).filter(Boolean),
  []);

  const fetchCvitpEntries = async (currentAccount) => {
    const active = currentAccount || account;
    if (!active) return;
    setIsLoadingEntries(true);
    try {
      const accessToken = await getValidToken(msalInstance, active);
      const res = await fetch(getApiUrl('/v1/cvitp'), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) { setTaxEntries(await res.json()); }
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error collecting CVITP datasets:', err);
    } finally { setIsLoadingEntries(false); }
  };

  const fetchEsignDetails = async (entry) => {
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/staff/esign-details/CVITP/${entry.id || entry._id}`), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) { setESignDetails(await res.json()); }
      else { const d = await res.json(); toast.error(`Could not fetch e-sign details: ${d.error || 'Not found'}`); }
    } catch (e) {
      console.error('Error fetching e-sign details:', e);
      toast.error('An error occurred while fetching e-sign details.');
    }
  };

  const handleOpenDrawerForAdd  = () => { setEditingEntry({}); setIsDrawerOpen(true); };
  const handleOpenDrawerForEdit = (r) => { setEditingEntry(r); setIsDrawerOpen(true); };
  const handleCloseDrawer       = () => { setEditingEntry(null); setIsDrawerOpen(false); };

  const handleDeleteEntry = async (entry) => {
    const id = entry.id;
    setIsSaving(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/cvitp/${id}`), {
        method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to delete entry.');
      setTaxEntries(prev => prev.filter(item => item.id !== id));
      handleCloseDrawer();
      toast.success('CVITP record deleted.');
    } catch (e) { toast.error(e.message || 'Could not delete record.'); }
    finally { setIsSaving(false); }
  };

  const handleSaveEntry = async (formData) => {
    setIsSaving(true);
    const isEditing = editingEntry && Object.keys(editingEntry).length > 0;
    const id = isEditing ? editingEntry.id : null;

    const cleanName = (formData.name || '').trim().toLowerCase();
    const cleanEmail = (formData.email || '').trim().toLowerCase();

    if (cleanName && cleanEmail) {
        const isDuplicate = taxEntries.some(entry => {
            return (entry.name || '').trim().toLowerCase() === cleanName &&
                   (entry.email || '').trim().toLowerCase() === cleanEmail &&
                   (!isEditing || entry.id !== id);
        });

        if (isDuplicate) {
            toast.error('Duplicate record found with the same name and email.');
            setIsSaving(false);
            return;
        }
    }
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const endpoint = isEditing ? `/v1/cvitp/${id}` : '/v1/cvitp';
      const method   = isEditing ? 'PUT' : 'POST';
      const payload  = { ...formData, yearsOfFiling: Array.isArray(formData.yearsOfFiling) ? formData.yearsOfFiling.join(', ') : formData.yearsOfFiling };
      const res = await fetch(getApiUrl(endpoint), {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(isEditing ? 'CVITP Tax Record updated successfully.' : 'CVITP Tax Record generated successfully.');
        handleCloseDrawer();
        fetchCvitpEntries();
      } else {
        const data = await res.json();
        toast.error(data.message || 'Operation failed execution.');
      }
    } catch { toast.error('Network error processing customer records.'); }
    finally { setIsSaving(false); }
  };

  const filteredEntries = useMemo(() => {
    const lower = searchText.toLowerCase();
    return taxEntries.filter(entry => {
      const statusMatch = filterStatus === 'nonCompleted'
        ? entry.status !== 'Completed' && entry.status !== 'Cancelled' && entry.status !== 'On Hold'
        : filterStatus ? entry.status === filterStatus : true;
      const assignedMatch = filterAssignedTo ? entry.assignedTo === filterAssignedTo : true;
      const matchStr = `${entry.name || ''} ${entry.mobile || ''} ${entry.email || ''} ${entry.assignedTo || ''} ${entry.status || ''}`.toLowerCase();
      return statusMatch && assignedMatch && (!lower || matchStr.includes(lower));
    });
  }, [taxEntries, searchText, filterStatus, filterAssignedTo]);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const sortedEntries = useMemo(() => {
    return [...filteredEntries].sort((a, b) => {
      let aV = a[sortKey] || '', bV = b[sortKey] || '';
      if (sortKey === 'receivedDate' || sortKey === 'filledDate') {
        const dA = new Date(aV), dB = new Date(bV);
        if (!isNaN(dA) && !isNaN(dB)) return sortAsc ? dA - dB : dB - dA;
      }
      if (typeof aV === 'string') aV = aV.toLowerCase();
      if (typeof bV === 'string') bV = bV.toLowerCase();
      if (aV < bV) return sortAsc ? -1 : 1;
      if (aV > bV) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filteredEntries, sortKey, sortAsc]);

  const assigneeStats = useMemo(() => calculateAssigneeStats(taxEntries), [taxEntries]);

  const completedStats = useMemo(() => {
    const stats = {};
    taxEntries.forEach(entry => {
      if (entry.status === 'Completed' && entry.assignedTo) {
        if (!stats[entry.assignedTo]) stats[entry.assignedTo] = 0;
        const years = entry.yearsOfFiling ? entry.yearsOfFiling.split(',').filter(Boolean) : [];
        stats[entry.assignedTo] += Math.max(1, years.length);
      }
    });
    return Object.entries(stats).sort(([, a], [, b]) => b - a);
  }, [taxEntries]);

  const totalCompletedFilingCount = useMemo(() =>
    completedStats.reduce((t, [, c]) => t + c, 0), [completedStats]);

  const totalPages = Math.max(1, Math.ceil(sortedEntries.length / pageSize));
  const pageData   = sortedEntries.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  useEffect(() => { setPageIndex(0); }, [pageSize, filterStatus, filterAssignedTo, searchText, taxEntries.length]);

  useEffect(() => {
    if (account && isInitialized) fetchCvitpEntries(account);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isInitialized]);

  const exportToCSV = () => {
    if (sortedEntries.length === 0) return;
    const headers = ['ID', 'Name', 'Mobile', 'Email', 'Status', 'Assigned To', 'Coin Reference', 'Received Date', 'Filed Date', 'Years of Filing', 'Created At'];
    const rows = sortedEntries.map(e => [e.id, e.name, e.mobile, e.email || '', e.status, e.assignedTo, e.coin, e.receivedDate, e.filledDate, e.yearsOfFiling || '', e.createdAt]);
    const csv = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', `CVITP_Clinic_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // Computed counts
  const nonCompletedCount = taxEntries.filter(e => e.status !== 'Completed' && e.status !== 'Cancelled' && e.status !== 'On Hold').length;
  const onHoldCount       = taxEntries.filter(e => e.status === 'On Hold').length;
  const completionPct     = taxEntries.length > 0 ? Math.round((taxEntries.filter(e => e.status === 'Completed').length / taxEntries.length) * 100) : 0;

  return (
    <TaxPortalLayout
      title="CVITP Clinic Desk"
      description="Manage community tax clinic records and client queues seamlessly."
      taxEntries={taxEntries}
      portalState={portalState}
      isFluid={false}
    >
      {account ? (
        <>
          {/* ══════════════ STAT CARDS ══════════════ */}
          <div className="row g-3 mb-4">

            {/* Card 1: Non-Completed */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card d-flex flex-column">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#f59e0b,#d97706)', boxShadow: '2px 0 10px rgba(245,158,11,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1 mb-2">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fef3c7,#fde68a)' }}>
                    <FaFolderOpen size={20} style={{ color: '#d97706' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Non-Completed</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>
                      {nonCompletedCount}<span className="fs-6 text-muted fw-normal ms-1">/ {taxEntries.length}</span>
                    </h2>
                  </div>
                </div>
                <button onClick={() => setShowOpenDetails(!showOpenDetails)} className="btn btn-link text-decoration-none p-0 text-muted fw-medium d-flex align-items-center gap-1 shadow-none position-relative z-1" style={{ fontSize: '12px' }}>
                  {showOpenDetails ? 'Show Less' : 'Show Details'}{showOpenDetails ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                </button>
                {showOpenDetails && (
                  <div className="flex-grow-1 overflow-auto position-relative z-1 border-top pt-3 mt-3" style={{ maxHeight: 130 }}>
                    <small className="text-muted fw-semibold d-block mb-2" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>BY ASSIGNEE</small>
                    {assigneeStats.map(([assignee, counts]) => (
                      <div key={assignee} className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-truncate fw-medium text-dark" style={{ fontSize: '0.76rem', maxWidth: 130 }} title={assignee}>{assignee.split('@')[0]}</span>
                        <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#1e40af', border: '1px solid #e2e8f0', fontSize: '0.68rem', padding: '3px 10px' }}>
                          {counts.pending} <span className="text-muted fw-normal">/ {counts.total}</span>
                        </span>
                      </div>
                    ))}
                    {assigneeStats.length === 0 && <span className="text-muted small">No assignments yet.</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Card 2: Completed filings + progress */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card d-flex flex-column">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#22c55e,#15803d)', boxShadow: '2px 0 10px rgba(34,197,94,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1 mb-2">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)' }}>
                    <FaCheckCircle size={20} style={{ color: '#15803d' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Completed Filings</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>{totalCompletedFilingCount}</h2>
                  </div>
                </div>
                <div className="position-relative z-1">
                  <div className="completion-bar-track">
                    <div className="completion-bar-fill" style={{ width: `${completionPct}%` }} />
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.68rem' }}>{completionPct}% overall completion</small>
                </div>
                <button onClick={() => setShowDoneDetails(!showDoneDetails)} className="btn btn-link text-decoration-none p-0 text-muted fw-medium d-flex align-items-center gap-1 shadow-none position-relative z-1 mt-2" style={{ fontSize: '12px' }}>
                  {showDoneDetails ? 'Show Less' : 'Show By Agent'}{showDoneDetails ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                </button>
                {showDoneDetails && (
                  <div className="flex-grow-1 overflow-auto position-relative z-1 border-top pt-3 mt-2" style={{ maxHeight: 110 }}>
                    {completedStats.map(([assignee, count]) => (
                      <div key={assignee} className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-truncate fw-medium text-dark" style={{ fontSize: '0.76rem', maxWidth: 130 }} title={assignee}>{assignee.split('@')[0]}</span>
                        <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', fontSize: '0.68rem', padding: '3px 10px' }}>{count}</span>
                      </div>
                    ))}
                    {completedStats.length === 0 && <span className="text-muted small">No completed filings yet.</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Card 3: On Hold */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#ef4444,#b91c1c)', boxShadow: '2px 0 10px rgba(239,68,68,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fee2e2,#fecaca)' }}>
                    <FaExclamationCircle size={20} style={{ color: '#b91c1c' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>On Hold</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>{onHoldCount}</h2>
                  </div>
                </div>
                <div className="mt-3 position-relative z-1">
                  <span className="badge rounded-pill fw-medium" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', fontSize: '0.68rem' }}>Needs attention</span>
                </div>
              </div>
            </div>

            {/* Card 4: Team queue */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white d-flex flex-column hover-lift stat-card position-relative overflow-hidden">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#6366f1,#4338ca)', boxShadow: '2px 0 10px rgba(99,102,241,0.3)' }} />
                <div className="d-flex align-items-center gap-2 mb-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)' }}>
                    <FaUsers size={15} style={{ color: '#4338ca' }} />
                  </div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Team queue · pending / total</small>
                </div>
                <div className="flex-grow-1 overflow-auto pe-1 position-relative z-1" style={{ maxHeight: 90 }}>
                  {assigneeStats.map(([assignee, counts]) => (
                    <div key={assignee} className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-truncate fw-medium text-dark" style={{ fontSize: '0.78rem', maxWidth: 140 }} title={assignee}>{assignee.split('@')[0]}</span>
                      <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#1e40af', border: '1px solid #e2e8f0', fontSize: '0.68rem', padding: '3px 10px' }}>
                        {counts.pending} <span className="text-muted fw-normal">/ {counts.total}</span>
                      </span>
                    </div>
                  ))}
                  {assigneeStats.length === 0 && <span className="text-muted small">No assignments yet.</span>}
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
            .hover-lift:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(15,23,42,0.10) !important; }
            .table-modern tbody tr { transition: background-color 0.15s ease; }
            .table-modern tbody tr:hover { background-color: #f8fafc; }
            .table-modern thead th { position: sticky; top: 0; z-index: 2; user-select: none; background: #fff; }
            .table-header-sep { border-bottom: 2px solid #e2e8f0 !important; }
            .year-chip { display: inline-flex; align-items: center; border-radius: 999px; padding: 2px 9px; font-size: 0.68rem; font-weight: 600; background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; white-space: nowrap; margin: 1px; }
            .assignee-chip { display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 3px 10px; font-size: 0.72rem; font-weight: 600; background: #ede9fe; color: #5b21b6; max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .date-row { display: flex; align-items: center; gap: 6px; font-size: 0.75rem; color: #64748b; line-height: 1.6; }
            .date-row svg { flex-shrink: 0; }
            .date-row .filled { color: #15803d; font-weight: 600; }
          `}</style>

          {/* ── Toolbar ── */}
          <TaxPortalToolbar
            searchPlaceholder="Search name, mobile, email..."
            searchText={searchText}
            onSearchChange={setSearchText}
            statusOptions={statusOptions}
            filterStatus={filterStatus}
            onFilterStatusChange={setFilterStatus}
            assignedToOptions={agentEmails}
            filterAssignedTo={filterAssignedTo}
            onFilterAssignedToChange={setFilterAssignedTo}
            pageSize={pageSize}
            onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
            onAdd={handleOpenDrawerForAdd}
            addLabel="Add Client"
            onExport={exportToCSV}
            disableExport={sortedEntries.length === 0}
            onRefresh={() => fetchCvitpEntries()}
            isRefreshing={isLoadingEntries}
          />

          <div className="position-relative" style={{ minHeight: '350px' }}>

            {/* ══════════════ MOBILE CARDS ══════════════ */}
            <div className="d-block d-md-none">
              {isLoadingEntries ? (
                <div className="d-flex flex-column gap-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="card border-0 rounded-4" style={{ overflow: 'hidden' }}>
                      <div className="skeleton-cell" style={{ height: 5, borderRadius: 0 }} />
                      <div className="card-body p-3">
                        <div className="d-flex gap-2 align-items-center mb-3">
                          <span className="skeleton-cell" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                          <div>
                            <span className="skeleton-cell d-block mb-1" style={{ width: 120, height: 13 }} />
                            <span className="skeleton-cell d-block" style={{ width: 80, height: 11 }} />
                          </div>
                        </div>
                        <span className="skeleton-cell" style={{ width: '100%', height: 32, borderRadius: 8 }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : pageData.length === 0 ? (
                <div className="card border-0 rounded-4 glass-card">
                  <div className="empty-state-wrap">
                    <div className="empty-state-icon"><FaInbox size={28} /></div>
                    <p className="mb-1 fw-semibold text-dark">No matching records</p>
                    <p className="mb-3 text-muted small text-center">Try adjusting your filters or search term.</p>
                    <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={handleOpenDrawerForAdd}>
                      <FaUserPlus className="me-2" size={12} /> Add Client
                    </button>
                  </div>
                </div>
              ) : (
                pageData.map((entry) => {
                  const statusMeta  = getStatusMeta(entry.status);
                  const avatarColor = getAvatarColor(entry.name || '');
                  return (
                    <div key={entry.id} className="card mb-3 mobile-record-card glass-card border-0 rounded-4 position-relative">
                      <div style={{ height: 5, background: statusMeta.color, borderRadius: '1rem 1rem 0 0' }} />
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className="name-avatar flex-shrink-0" style={{ backgroundColor: avatarColor.bg, color: avatarColor.text, width: 36, height: 36, fontSize: 12 }}>
                              {getInitials(entry.name || '')}
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">{entry.name}</h6>
                              {entry.mobile && (
                                <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => { setDialNumber(entry.mobile); setIsDialerOpen(true); }}>
                                  <span className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: '0.75rem' }}><FaPhoneAlt size={9} /> {entry.mobile}</span>
                                </button>
                              )}
                              {entry.email && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{entry.email}</div>}
                            </div>
                          </div>
                          <div className="dropdown">
                            <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                              <FaEllipsisV size={13} />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                              <li><button className="dropdown-item" onClick={() => handleOpenDrawerForEdit(entry)}><span className="d-flex align-items-center gap-2"><FaEdit /> Edit Details</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: entry, action: 'requestDetails', taxType: 'CVITP' })}><span className="d-flex align-items-center gap-2"><FaClipboardList /> Request Details</span></button></li>
                              <li><button className="dropdown-item" onClick={() => navigate('/esign-request', { state: { customerId: entry.id, clientName: entry.name, clientEmail: entry.email || '', taxType: 'CVITP' } })}><span className="d-flex align-items-center gap-2"><FaSignature /> Request eSign</span></button></li>
                              <li><button className="dropdown-item" onClick={() => fetchEsignDetails(entry)}><span className="d-flex align-items-center gap-2"><FaChartBar /> eSign Details</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: entry, action: 'requestDocument', taxType: 'CVITP' })}><span className="d-flex align-items-center gap-2"><FaFolderOpen /> Request Document</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: entry, action: 'sendConfirmation', taxType: 'CVITP' })}><span className="d-flex align-items-center gap-2"><FaPaperPlane /> Send Confirmation</span></button></li>
                            </ul>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <StatusBadge status={entry.status} size="sm" />
                          {entry.yearsOfFiling && <span className="year-chip">{entry.yearsOfFiling}</span>}
                        </div>
                        {entry.assignedTo && (
                          <div className="assignee-chip" title={entry.assignedTo}>
                            <FaUsers size={9} />{entry.assignedTo.split('@')[0]}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ══════════════ DESKTOP TABLE ══════════════ */}
            <div className="table-responsive d-none d-md-block rounded-4 shadow-sm bg-white" style={{ minHeight: '350px' }}>
              <table className="table table-modern table-hover align-middle mb-0" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr className="table-header-sep text-muted text-uppercase" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
                    {/* NAME_TH_PL = 16 + 32 + 8 = 56px, matching td ps-3 + avatar + gap */}
                    <th className="py-3 border-0" style={{ paddingLeft: NAME_TH_PL, cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      Name & Contact <SortIcon active={sortKey === 'name'} asc={sortAsc} />
                    </th>
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('receivedDate')}>Dates <SortIcon active={sortKey === 'receivedDate'} asc={sortAsc} /></th>
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('yearsOfFiling')}>Years <SortIcon active={sortKey === 'yearsOfFiling'} asc={sortAsc} /></th>
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status <SortIcon active={sortKey === 'status'} asc={sortAsc} /></th>
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('assignedTo')}>Assigned To <SortIcon active={sortKey === 'assignedTo'} asc={sortAsc} /></th>
                    <th className="py-3 border-0">Action</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {isLoadingEntries ? (
                    <SkeletonRows count={8} />
                  ) : pageData.length === 0 ? (
                    <tr>
                      <td colSpan="6">
                        <div className="empty-state-wrap">
                          <div className="empty-state-icon"><FaInbox size={28} /></div>
                          <p className="mb-1 fw-semibold text-dark">No CVITP records found</p>
                          <p className="mb-3 text-muted small text-center" style={{ maxWidth: 320 }}>
                            No records match your current filters. Try adjusting your search or status filter.
                          </p>
                          <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={handleOpenDrawerForAdd}>
                            <FaUserPlus className="me-2" size={12} /> Add Client
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pageData.map((entry) => (
                      <tr key={entry.id}>
                        {/* Name cell: ps-3 (16px) + avatar (32px) + gap-2 (8px) = 56px → aligns with th */}
                        <td className="ps-3" style={{ overflow: 'hidden' }}>
                          <div className="d-flex align-items-center gap-2">
                            <ClientAvatar name={entry.name || ''} />
                            <div style={{ minWidth: 0 }}>
                              <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.875rem' }} title={entry.name}>{entry.name}</div>
                              {entry.mobile && (
                                <button className="btn btn-link btn-sm p-0 text-decoration-none" style={{ fontSize: '0.78rem' }} onClick={() => { setDialNumber(entry.mobile); setIsDialerOpen(true); }}>
                                  <span className="d-flex align-items-center gap-1"><FaPhoneAlt size={9} /> {entry.mobile}</span>
                                </button>
                              )}
                              {entry.email && (
                                <div className="text-muted text-truncate d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }} title={entry.email}>
                                  <FaEnvelope size={8} />{entry.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Dates */}
                        <td>
                          <div className="date-row">
                            <FaCalendarAlt size={10} />
                            <span>{fmtDate(entry.receivedDate) || <span className="text-muted">Not set</span>}</span>
                          </div>
                          {entry.filledDate && (
                            <div className="date-row">
                              <FaCalendarCheck size={10} className="filled" style={{ color: '#15803d' }} />
                              <span className="filled">{fmtDate(entry.filledDate)}</span>
                            </div>
                          )}
                          {entry.updatedAt && (
                            <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 2 }}>
                              Updated {new Date(entry.updatedAt).toLocaleDateString()}
                            </div>
                          )}
                        </td>

                        {/* Years of filing as chips */}
                        <td>
                          {entry.yearsOfFiling
                            ? entry.yearsOfFiling.split(',').map(y => y.trim()).filter(Boolean).map(y => (
                              <span key={y} className="year-chip">{y}</span>
                            ))
                            : <span className="text-muted">—</span>}
                        </td>

                        {/* Status + notes tooltip */}
                        <td>
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <StatusBadge status={entry.status} />
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 ms-1 text-muted"
                              aria-label="Notes"
                              onClick={() => setActiveTooltipId(activeTooltipId === entry.id ? null : entry.id)}
                              onMouseEnter={() => setActiveTooltipId(entry.id)}
                              onMouseLeave={() => setActiveTooltipId(null)}
                            >
                              <FaInfoCircle size={13} />
                            </button>
                            {activeTooltipId === entry.id && (
                              <div role="tooltip" className="shadow-sm" style={{ position: 'absolute', top: 30, left: 0, background: '#fff', padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', minWidth: 200, zIndex: 2000, fontSize: '12px', color: '#374151', lineHeight: 1.5 }}>
                                {entry.notes || <span className="text-muted fst-italic">No notes available</span>}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Assigned To */}
                        <td>
                          {entry.assignedTo
                            ? <span className="assignee-chip" title={entry.assignedTo}><FaUsers size={9} />{entry.assignedTo.split('@')[0]}</span>
                            : <span className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>Unassigned</span>}
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="dropdown">
                            <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                              <FaEllipsisV size={13} />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                              <li><button className="dropdown-item" onClick={() => handleOpenDrawerForEdit(entry)}><span className="d-flex align-items-center gap-2"><FaEdit /> Edit Details</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: entry, action: 'requestDetails', taxType: 'CVITP' })}><span className="d-flex align-items-center gap-2"><FaClipboardList /> Request Details</span></button></li>
                              <li><button className="dropdown-item" onClick={() => navigate('/esign-request', { state: { customerId: entry.id, clientName: entry.name, clientEmail: entry.email || '', taxType: 'CVITP' } })}><span className="d-flex align-items-center gap-2"><FaSignature /> Request eSign</span></button></li>
                              <li><button className="dropdown-item" onClick={() => fetchEsignDetails(entry)}><span className="d-flex align-items-center gap-2"><FaChartBar /> eSign Details</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: entry, action: 'requestDocument', taxType: 'CVITP' })}><span className="d-flex align-items-center gap-2"><FaFolderOpen /> Request Document</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: entry, action: 'sendConfirmation', taxType: 'CVITP' })}><span className="d-flex align-items-center gap-2"><FaPaperPlane /> Send Confirmation</span></button></li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ══════════════ PAGINATION ══════════════ */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mt-4 px-1">
              <div className="d-flex align-items-center gap-2">
                <span className="rounded-pill px-3 py-1 fw-semibold" style={{ background: '#f1f5f9', color: '#374151', fontSize: '0.78rem', border: '1px solid #e2e8f0' }}>
                  {sortedEntries.length === 0 ? '0' : `${pageIndex * pageSize + 1}–${Math.min(sortedEntries.length, (pageIndex + 1) * pageSize)}`}
                  <span className="text-muted fw-normal"> of </span>
                  {sortedEntries.length}
                </span>
                <span className="text-muted" style={{ fontSize: '0.78rem' }}>records</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <button className="page-pill" disabled={pageIndex === 0} onClick={() => setPageIndex(p => Math.max(p - 1, 0))} aria-label="Previous page">
                  <FaChevronLeft size={10} /> Prev
                </button>
                {(() => {
                  if (totalPages <= 1) return null;
                  const items = [];
                  const addPage = (i) => items.push(
                    <button key={i} className={`page-pill${i === pageIndex ? ' active' : ''}`} onClick={() => setPageIndex(i)} aria-label={`Page ${i + 1}`} aria-current={i === pageIndex ? 'page' : undefined}>
                      {i + 1}
                    </button>
                  );
                  const addDots = (key) => items.push(<span key={key} className="page-dots">···</span>);
                  if (totalPages <= 7) { for (let i = 0; i < totalPages; i++) addPage(i); }
                  else {
                    addPage(0);
                    if (pageIndex > 2) addDots('l');
                    for (let i = Math.max(1, pageIndex - 1); i <= Math.min(totalPages - 2, pageIndex + 1); i++) addPage(i);
                    if (pageIndex < totalPages - 3) addDots('r');
                    addPage(totalPages - 1);
                  }
                  return items;
                })()}
                <button className="page-pill" disabled={pageIndex >= totalPages - 1} onClick={() => setPageIndex(p => Math.min(p + 1, totalPages - 1))} aria-label="Next page">
                  Next <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          </div>

          <CvitpDrawer
            isOpen={isDrawerOpen}
            onClose={handleCloseDrawer}
            entry={editingEntry}
            onSave={handleSaveEntry}
            onDelete={handleDeleteEntry}
            saving={isSaving}
            isAdmin={isAdminRole(account?.username)}
            isDialerOpen={isDialerOpen}
          />
          <ESignDetailsModal details={eSignDetails} onClose={() => setESignDetails(null)} />

          <Loader variant="overlay" show={isSaving && !isLoadingEntries} text="Saving changes..." />
        </>
      ) : (
        <div className="alert alert-info border-0 p-4 shadow-sm mt-4">
          <h5 className="fw-bold">🔐 Access Required</h5>
          Please sign in with your Oriental Biz account to access the CVITP Clinic Desk.
        </div>
      )}
    </TaxPortalLayout>
  );
};

export default CvitpPage;
